import { Match, Player, Tournament } from "../../components/handball";

const BASE_RATING = 1200;
const K = 32;

function expectedScore(rA: number, rB: number) {
  return 1 / (1 + Math.pow(10, (rB - rA) / 400));
}

function applyElo(rA: number, rB: number, aWon: boolean) {
  const Ea = expectedScore(rA, rB);
  const Sa = aWon ? 1 : 0;
  const newA = rA + K * (Sa - Ea);
  const Eb = expectedScore(rB, rA);
  const Sb = aWon ? 0 : 1;
  const newB = rB + K * (Sb - Eb);
  return [newA, newB] as const;
}

function sortMatchesChronologically(ms: Match[], ts: Tournament[]) {
  const tMap = new Map(ts.map(t => [t.id, t] as const));
  return [...ms].sort((a, b) => {
    const ta = tMap.get(a.tournamentId)!;
    const tb = tMap.get(b.tournamentId)!;
    if (ta.date !== tb.date) return ta.date.localeCompare(tb.date);
    return a.round - b.round;
  });
}

export function computeRatings(allPlayers: Player[], allMatches: Match[], allTournaments: Tournament[]) {
  const rating = new Map<string, number>();
  const wins = new Map<string, number>();
  const losses = new Map<string, number>();

  allPlayers.forEach(p => {
    rating.set(p.id, BASE_RATING);
    wins.set(p.id, 0);
    losses.set(p.id, 0);
  });

  const chronological = sortMatchesChronologically(allMatches, allTournaments);

  for (const m of chronological) {
    const r1 = rating.get(m.player1Id)!;
    const r2 = rating.get(m.player2Id)!;
    const p1Won = m.winnerId === m.player1Id;
    const [new1, new2] = applyElo(r1, r2, p1Won);
    rating.set(m.player1Id, new1);
    rating.set(m.player2Id, new2);
    if (p1Won) {
      wins.set(m.player1Id, (wins.get(m.player1Id) || 0) + 1);
      losses.set(m.player2Id, (losses.get(m.player2Id) || 0) + 1);
    } else {
      wins.set(m.player2Id, (wins.get(m.player2Id) || 0) + 1);
      losses.set(m.player1Id, (losses.get(m.player1Id) || 0) + 1);
    }
  }

  return allPlayers.map(p => ({
    player: p,
    rating: rating.get(p.id) || BASE_RATING,
    wins: wins.get(p.id) || 0,
    losses: losses.get(p.id) || 0,
  })).sort((a, b) => b.rating - a.rating);
}


